import React, {
  PropsWithChildren,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react';

import { hashHistory, RouteComponentProps } from 'react-router';

import { LoadingSpinner } from '../../components/layouts';
import { ErrorFallback } from '../../components/layouts/ErrorFallback';
import {
  AppConfig,
  WebToAppRedirect,
} from '../../core/api/whitelabel/whitelabel.types';
import { analyticsService } from '../../core/services';
import { urlUtils } from '../../utils';
import { storage as storageService } from '../../weborder/services/storageServices';
import { Utilities } from '../../weborder/Utilities';
import { DispatchProps, StateProps } from './index';
import { setCookieUtils } from './setCookieUtils';

type LoadStatus = 'NOT_LOADED' | 'LOADING' | 'LOADED';

interface EnrichedChildren {
  children?: ReactNode;
  parentComponent: ReactNode;
}

type PropsType = StateProps &
  DispatchProps &
  RouteComponentProps<Record<string, unknown>, Record<string, unknown>>;

export function SetCookie(props: PropsWithChildren<PropsType>): JSX.Element {
  const {
    addToast,
    appConfig,
    children,
    collectionSummaries,
    customer,
    deliverySummaries,
    isClientAnalyticsLoaded,
    loadCollectionSummaries,
    loadDeliverySummaries,
    location,
    params,
    setRestaurantUrl,
    showRestaurantList,
    translate,
    updateRouterState,
  } = props;

  const [isRedirected, setIsRedirected] = useState(false);
  const [vrId, setVrId] = useState(0);

  const pathnameRef = useRef(location.pathname);
  const loadStoreListStatus = useRef<LoadStatus>('NOT_LOADED');

  useEffect(() => {
    let virtualRestaurantId = 0;

    if (collectionSummaries.status === 'success') {
      virtualRestaurantId = collectionSummaries.data[0]?.VirtualRestaurantId;
    } else if (deliverySummaries.status === 'success') {
      virtualRestaurantId = deliverySummaries.data[0]?.VirtualRestaurantId;
    }

    setVrId(virtualRestaurantId);
  }, [collectionSummaries.status, deliverySummaries.status]);

  useEffect(() => {
    const isAppReadyToLoadStoreData =
      isClientAnalyticsLoaded &&
      appConfig.status === 'success' &&
      loadStoreListStatus.current === 'NOT_LOADED';

    const initApp = () => {
      if (isAppReadyToLoadStoreData) {
        initialiseStoreList();
      }
    };

    initApp();
  }, []);

  useEffect(() => {
    const routerState = {
      params,
      pathname: location.pathname,
    };
    updateRouterState(routerState);
    const isMobileDevice = Utilities.isAndroid || Utilities.isIOSPhone;
    if (
      location.pathname === '/' &&
      (pathnameRef.current !== location.pathname ||
        (isMobileDevice && WebToAppRedirect.RedirectAlways))
    ) {
      hashHistory.replace('/where');
    }

    pathnameRef.current = location.pathname;
  }, [location.pathname]);

  useEffect(() => {
    if (customer.status === 'success') {
      addToast(translate('Logged_in') as string);
    }
  }, [customer.status]);

  useEffect(() => {
    const getRedirectUrl = (configData: AppConfig): string => {
      let redirectUrl = '';
      if (Utilities.isAndroid && configData.PlayStoreUrl) {
        redirectUrl = configData.PlayStoreUrl;
      } else if (Utilities.isIOSPhone && configData.AppStoreUrl) {
        redirectUrl = configData.AppStoreUrl;
      }
      return redirectUrl;
    };
    const handleRedirect = (redirectUrl: string): void => {
      if (redirectUrl !== '') {
        setIsRedirected(true);
        urlUtils.assignUrl(redirectUrl);
      }
    };
    const handleOnceRedirectAppStore = (configData: AppConfig): void => {
      let redirectUrl = '';
      const isMobileDevice = Utilities.isAndroid || Utilities.isIOSPhone;
      const eventData = {
        amp_label: 'Mobile App Redirect Triggered',
        amp_redirectedToMobile: false,
      };

      if (
        isMobileDevice &&
        storageService.getItem<boolean>('flipdish-redirect') === undefined
      ) {
        // eslint-disable-next-line no-alert
        const redirectConfirm = window.confirm(
          translate('Redirect_confirm') as string,
        );

        if (redirectConfirm) {
          eventData.amp_redirectedToMobile = true;
          redirectUrl = getRedirectUrl(configData);
        }

        analyticsService.track(eventData);
      }
      Utilities.setInLocalStorage('flipdish-redirect', false);
      handleRedirect(redirectUrl);
    };
    const handleAlwaysRedirectToAppStore = (configData: AppConfig): void => {
      const eventData = {
        amp_label: 'Mobile App Redirect Triggered',
      };

      const redirectUrl = getRedirectUrl(configData);
      if (redirectUrl) {
        analyticsService.track(eventData);
      }
      handleRedirect(redirectUrl);
    };

    if (appConfig.status === 'success' && !isRedirected) {
      const webToAppRedirectSetting =
        parseInt(Utilities.extraOption('data-webtoappredirect')) ||
        appConfig.data.WebToAppRedirect;

      switch (webToAppRedirectSetting) {
        case WebToAppRedirect.RedirectOnce:
          handleOnceRedirectAppStore(appConfig.data);
          break;
        case WebToAppRedirect.RedirectAlways:
          handleAlwaysRedirectToAppStore(appConfig.data);
          break;
        default:
          break;
      }
    }
  }, [appConfig.status]);

  useEffect(() => {
    if (
      isClientAnalyticsLoaded &&
      appConfig.status === 'success' &&
      loadStoreListStatus.current === 'NOT_LOADED'
    ) {
      initialiseStoreList();
    }
  }, [appConfig.status, isClientAnalyticsLoaded, loadStoreListStatus.current]);

  useEffect(() => {
    if (appConfig.status === 'success') {
      if (!appConfig.data.DeliveryEnabled && !appConfig.data.PickupEnabled) {
        setIsRedirected(true);
        setCookieUtils.redirectToUnavailable();
      } else if (!isRedirected && vrId !== 0) {
        const collectionSumariesData =
          collectionSummaries.status === 'success'
            ? collectionSummaries.data
            : [];

        setIsRedirected(true);

        if (location.pathname === '/') {
          setCookieUtils.redirectToStore(
            appConfig.data,
            collectionSumariesData,
            setRestaurantUrl,
            showRestaurantList,
            vrId,
          );
        }
      }
    }
  }, [appConfig.status, collectionSummaries.status, vrId]);

  const initialiseStoreList = (): void => {
    if (appConfig.status === 'success') {
      loadStoreListStatus.current = 'LOADING';
      if (appConfig.data.PickupEnabled) {
        loadCollectionSummaries();
      }
      if (appConfig.data.DeliveryEnabled) {
        loadDeliverySummaries();
      }
      loadStoreListStatus.current = 'LOADED';
    }
  };

  if (
    collectionSummaries.status === 'none' &&
    deliverySummaries.status === 'none'
  ) {
    return <ErrorFallback logIdentifier="set-cookie" />;
  }

  if (!isRedirected || !children) {
    return <LoadingSpinner />;
  }

  // @ts-ignore React.Children typing is problematic :-(
  return React.Children.map(children, (child: ReactNode) => {
    if (!React.isValidElement<EnrichedChildren>(child)) {
      return child;
    }

    return React.cloneElement(child, {
      parentComponent: this,
    });
  });
}
